import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  AppFeature,
  GetHealthProviderFeaturesDocument,
  HealthProviderFeatureItemDataFragment,
  HealthProviderItemDataFragment,
  UpdateHealthProviderFeatureItemDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputSwitchController } from '../../../../elements/form/controllers/input-switch';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { useHealthProviderFeatureForm } from '../../form/use-health-provider-feature-form';

type HealthProviderFeatureModal = {
  healthProvider: HealthProviderItemDataFragment;
  healthProviderFeatureItemData: HealthProviderFeatureItemDataFragment;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function HealthProviderFeatureModal({
  healthProvider,
  healthProviderFeatureItemData,
  onSaved,
  onCancel,
  onClose,
}: HealthProviderFeatureModal) {
  const intl = useIntl();

  const { form, formIsValid, setErrors } = useHealthProviderFeatureForm();
  const { showAlertMessage } = useAppContext();

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();
        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  useEffect(() => {
    if (healthProviderFeatureItemData) {
      form.reset({
        replyToExistingChats:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.ReplyToExistingChats
          ) ?? false,
        mind:
          healthProviderFeatureItemData.appFeatures.includes(AppFeature.Mind) ??
          false,
        startChat:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.StartChat
          ) ?? false,
        startMedicationRequest:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.StartMedicationRequest
          ) ?? false,
      });
    }
  }, [healthProviderFeatureItemData, form]);

  const [
    updateHealthProviderFeatureItem,
    { loading: updateHealthProviderFeatureItemLoading },
  ] = useMutation(UpdateHealthProviderFeatureItemDocument, {
    refetchQueries: [GetHealthProviderFeaturesDocument],
  });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      const features = [];

      if (values.replyToExistingChats === true) {
        features.push(AppFeature.ReplyToExistingChats);
      }
      if (values.mind === true) {
        features.push(AppFeature.Mind);
      }
      if (values.startChat === true) {
        features.push(AppFeature.StartChat);
      }
      if (values.startMedicationRequest === true) {
        features.push(AppFeature.StartMedicationRequest);
      }

      try {
        const result = await updateHealthProviderFeatureItem({
          variables: {
            features: {
              appFeatures: features,
              availabilityStatus:
                healthProviderFeatureItemData.availabilityStatus,
            },
            healthProviderId: healthProvider.id ?? '',
          },
        });
        if (result.data?.healthProviderFeaturesMutate?.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'health-provider-translation.health-provider-translation-update.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          setErrors(result.data?.healthProviderFeaturesMutate?.errors);
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    healthProvider.id,
    healthProviderFeatureItemData.availabilityStatus,
    intl,
    onClose,
    onSaved,
    setErrors,
    showAlertMessage,
    updateHealthProviderFeatureItem,
  ]);
  return (
    <>
      <Modal
        testId="health-provider-feature-modal"
        loading={false}
        title={healthProviderFeatureItemData.availabilityStatus ?? ''}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                updateHealthProviderFeatureItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.NONE
              }
              disabled={
                !formIsValid ||
                !form.formState.isDirty ||
                updateHealthProviderFeatureItemLoading
              }
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.change" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="common.practice" />
            </h4>
            {healthProvider?.name ?? ''}
          </div>
          <FormRow>
            <FormRow>
              <InputSwitchController
                name="mind"
                label={
                  <CustomFormattedMessage
                    id={`common.feature.${kebabCase(AppFeature.Mind)}`}
                  />
                }
              />
            </FormRow>
            <InputSwitchController
              name="replyToExistingChats"
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(
                    AppFeature.ReplyToExistingChats
                  )}`}
                />
              }
            />
          </FormRow>

          <FormRow>
            <InputSwitchController
              name="startChat"
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(AppFeature.StartChat)}`}
                />
              }
            />
          </FormRow>

          <FormRow>
            <InputSwitchController
              name="startMedicationRequest"
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(
                    AppFeature.StartMedicationRequest
                  )}`}
                />
              }
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
